/**
 * Centralized route path constants for the application.
 * Use these constants instead of hardcoding paths throughout the codebase.
 *
 * @example
 * import { ROUTES } from '@/config/routes';
 * navigate(ROUTES.AUTH.LOGIN);
 * navigate(ROUTES.PROJECTS.DETAIL('123'));
 */

// ============================================================================
// AUTH ROUTES
// ============================================================================
export const AUTH_ROUTES = {
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REGISTER: '/auth/register',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  CHANGE_PASSWORD: '/auth/change-password',
  TWO_FACTOR: '/auth/two-factor',
  TWO_FACTOR_SETUP: '/auth/two-factor-setup',
  ONBOARDING: '/auth/onboarding',
} as const;

// ============================================================================
// MAIN ROUTES (Non-domain specific)
// ============================================================================
export const MAIN_ROUTES = {
  HOME: '/',
  PROFILE: '/profile',
  SECURITY_SETTINGS: '/security-settings',
  NOT_FOUND: '/404',
} as const;

/** Apps page — desktop equivalent of the mobile Hub launcher */
export const APPS_ROUTES = {
  BASE: '/app/apps',
  INDEX: '/app/apps',
} as const;

// ============================================================================
// DOMAIN ROUTES
// ============================================================================

/** Projects domain routes */
export const PROJECTS_ROUTES = {
  BASE: '/app/projects',
  DASHBOARD: '/app/projects',
  LIST: '/app/projects/list',
  // Project list scoped to a single ProjectOrigin (Self/Org/Dept/Location).
  // The `originId` matches `ProjectOrigin.originID` from the lookup; the list
  // page reads it from the query string. Members still only see their own
  // assigned projects — the origin just narrows the scope.
  LIST_BY_ORIGIN: (originId: string | number) =>
    `/app/projects/list?originId=${originId}` as const,
  BOARD: '/app/projects/board',
  CALENDAR: '/app/projects/calendar',
  MY_PROJECTS: '/app/projects/my-projects',
  REPORTS: '/app/projects/reports',
  PORTFOLIOS: '/app/projects/portfolios',
  CREATE: '/app/projects/create',
  // Detail/edit pages live in the `view/[id]` and `edit/[id]` folders, so the
  // URLs must be `/view/:id` and `/edit/:id` (not `/:id` / `/:id/edit`).
  VIEW: (id: string | number) => `/app/projects/view/${id}` as const,
  DETAIL: (id: string | number) => `/app/projects/view/${id}` as const,
  EDIT: (id: string | number) => `/app/projects/edit/${id}` as const,
  // Admin section
  ADMIN: '/app/projects/admin',
  ADMIN_DASHBOARD: '/app/projects/admin/dashboard',
  LOOKUPS: '/app/projects/admin/lookups',
  LOOKUP_CATEGORY: (category: string) =>
    `/app/projects/admin/lookups/${category}` as const,
  LOOKUP_ITEM: (category: string, lookup: string) =>
    `/app/projects/admin/lookups/${category}/${lookup}` as const,
  ADMIN_SETTINGS: '/app/projects/admin/settings',
} as const;

/** Tasks domain routes */
export const TASKS_ROUTES = {
  BASE: '/app/tasks',
  DASHBOARD: '/app/tasks',
  BOARD: '/app/tasks/board',
  MY_TASKS: '/app/tasks/my-tasks',
  // The task detail page is routed at `/view/:id`; `/app/tasks/:id` falls through to the
  // catch-all and bounces to the dashboard.
  DETAIL: (id: string) => `/app/tasks/view/${id}` as const,
  CREATE: '/app/tasks/create',
  CREATE_FOR_PROJECT: (projectId: string | number) => `/app/tasks/create?projectId=${projectId}` as const,
} as const;

/** Members domain routes */
export const MEMBERS_ROUTES = {
  BASE: '/app/members',
  DASHBOARD: '/app/members',
  LIST: '/app/members/list',
  DETAIL: (id: string) => `/app/members/${id}` as const,
} as const;

/** Users domain routes */
export const USERS_ROUTES = {
  BASE: '/app/users',
  DASHBOARD: '/app/users',
  LIST: '/app/users/list',
  DETAIL: (id: string) => `/app/users/${id}` as const,
  ROLES: '/app/users/roles',
} as const;

/** Procurements domain routes */
export const PROCUREMENTS_ROUTES = {
  BASE: '/app/procurements',
  DASHBOARD: '/app/procurements',
  LIST: '/app/procurements/list',
  DETAIL: (id: string) => `/app/procurements/${id}` as const,
  CREATE: '/app/procurements/create',
} as const;

/** Meetings domain routes */
export const MEETINGS_ROUTES = {
  BASE: '/app/meetings',
  DASHBOARD: '/app/meetings',
  LIST: '/app/meetings/list',
  MY_MEETINGS: '/app/meetings/my-meetings',
  INVITATIONS: '/app/meetings/invitations',
  MY_ACTION_ITEMS: '/app/meetings/my-action-items',
  CALENDAR: '/app/meetings/calendar',
  CREATE: '/app/meetings/create',
  CREATE_FOR_DATE: (date: string | number) => `/app/meetings/create?date=${date}` as const,
  BOOK_VENUE: '/app/meetings/book-venue',
  VIEW: (id: string | number) => `/app/meetings/view/${id}` as const,
  DETAIL: (id: string | number) => `/app/meetings/view/${id}` as const,
  EDIT: (id: string | number) => `/app/meetings/edit/${id}` as const,
  LIST_BY_PROJECT: (projectId: string | number) => `/app/meetings/list?projectId=${projectId}` as const,
  ADMIN: '/app/meetings/admin',
  ADMIN_ALL_MEETINGS: '/app/meetings/admin/all-meetings',
  ADMIN_BOOKINGS: '/app/meetings/admin/bookings',
  ADMIN_VENUES: '/app/meetings/admin/venues',
  ADMIN_REPORTS: '/app/meetings/admin/reports',
  LOOKUPS: '/app/meetings/admin/lookups',
  ADMIN_LOOKUPS: '/app/meetings/admin/lookups',
  ADMIN_SETTINGS: '/app/meetings/admin/settings',
} as const;

/** Campus Report domain routes */
export const CAMPUS_REPORT_ROUTES = {
  BASE: '/app/campus-report',
  DASHBOARD: '/app/campus-report',
} as const;

/** QR Codes domain routes */
export const QRCODES_ROUTES = {
  BASE: '/app/qr-codes',
  DASHBOARD: '/app/qr-codes',
  LIST: '/app/qr-codes/list',
  MY_CODES: '/app/qr-codes/my-codes',
  CREATE: '/app/qr-codes/create',
  VIEW: (id: string | number) => `/app/qr-codes/view/${id}` as const,
  EDIT: (id: string | number) => `/app/qr-codes/edit/${id}` as const,
  ADMIN_LOOKUPS: '/app/qr-codes/admin/lookups',
  ADMIN_FRAMES: '/app/qr-codes/admin/frames',
  ADMIN_ASSETS: '/app/qr-codes/admin/assets',
} as const;

/** Announcements domain routes */
export const ANNOUNCEMENTS_ROUTES = {
  BASE: '/app/announcements',
  DASHBOARD: '/app/announcements',
  LIST: '/app/announcements/list',
  DETAIL: (id: string) => `/app/announcements/view/${id}` as const,
  ADMIN: '/app/announcements/admin',
  LOOKUPS: '/app/announcements/admin/lookups',
} as const;

/** Admin routes */
export const ADMIN_ROUTES = {
  USERS: '/admin/users',
  MEMBERS: '/admin/members',
  PERMISSIONS: '/admin/permissions',
  REPORTS: '/admin/reports',
  SYSTEM_LOG: '/admin/system-log',
  SYSTEM: '/admin/system',
  // App Catalogue — now a full domain app with its own left sidebar.
  // APP_CATALOGUE is the index (Overview); APP_CATALOGUE_APPS is the list page.
  APP_CATALOGUE: '/admin/app-catalogue',
  // LLM (MCP) connector operations: switches, sessions, audit log, clients.
  CONNECTOR: '/admin/connector',
  // Console-issued API keys for external integrations: issue / renew / rotate / revoke.
  API_KEYS: '/admin/api-keys',
  APP_CATALOGUE_OVERVIEW: '/admin/app-catalogue',
  APP_CATALOGUE_APPS: '/admin/app-catalogue/apps',
  APP_CATALOGUE_RECOMMENDATIONS: '/admin/app-catalogue/recommendations',
  APP_CATALOGUE_ENTITLEMENTS: '/admin/app-catalogue/entitlements',
  APP_CATALOGUE_FEATURES: '/admin/app-catalogue/features',
  APP_CATALOGUE_NEW: '/admin/app-catalogue/apps/new',
  APP_CATALOGUE_EDIT: (appId: string) => `/admin/app-catalogue/apps/edit/${appId}` as const,
  // Private distribution: how each app reaches a phone, and the redemption-code pool
  // behind the iOS ones.
  APP_CATALOGUE_DISTRIBUTION: '/admin/app-catalogue/distribution',
  APP_CATALOGUE_DISTRIBUTION_DETAIL: (appId: string) =>
    `/admin/app-catalogue/distribution/${appId}` as const,
  APP_CATALOGUE_CODES: '/admin/app-catalogue/codes',
  APP_CATALOGUE_CODES_DETAIL: (appId: string) => `/admin/app-catalogue/codes/${appId}` as const,
} as const;

/** Demo domain routes */
export const DEMO_ROUTES = {
  BASE: '/app/demo',
  DASHBOARD: '/app/demo/dashboard',
  // UI Components
  UI: {
    CORE: '/app/demo/ui/core',
    INPUTS: '/app/demo/ui/inputs',
    DATA_DISPLAY: '/app/demo/ui/data-display',
    FEEDBACK: '/app/demo/ui/feedback',
    ALERTS: '/app/demo/ui/alerts',
    NAVIGATION: '/app/demo/ui/navigation',
    OVERLAYS: '/app/demo/ui/overlays',
    LAYOUT: '/app/demo/ui/layout',
  },
  // Apps
  APPS: {
    INDEX: '/app/demo/apps',
    ECOMMERCE: '/app/demo/apps/ecommerce',
    PROJECT_MANAGEMENT: '/app/demo/apps/project-management',
    CRM: '/app/demo/apps/crm',
    HR_PORTAL: '/app/demo/apps/hr-portal',
    FILE_MANAGER: '/app/demo/apps/file-manager',
    ANALYTICS: '/app/demo/apps/analytics',
    CHAT: '/app/demo/apps/chat',
    CALENDAR: '/app/demo/apps/calendar',
    INVOICING: '/app/demo/apps/invoicing',
    HELPDESK: '/app/demo/apps/helpdesk',
  },
  // Advanced
  ADVANCED: {
    CALENDAR: '/app/demo/advanced/calendar',
    WORKFLOW: '/app/demo/advanced/workflow',
    EDITOR: '/app/demo/advanced/editor',
    DATA_TABLES: '/app/demo/advanced/data-tables',
    FORM_BUILDER: '/app/demo/advanced/form-builder',
  },
  // Animations
  ANIMATIONS: {
    TRANSITIONS: '/app/demo/animations/transitions',
    INTERACTIVE: '/app/demo/animations/interactive',
    SCROLL: '/app/demo/animations/scroll',
  },
  // Auth Pages Demo
  AUTH_PAGES: {
    INDEX: '/app/demo/auth-pages',
    LOGIN: '/app/demo/auth-pages/login',
    REGISTER: '/app/demo/auth-pages/register',
    FORGOT_PASSWORD: '/app/demo/auth-pages/forgot-password',
    RESET_PASSWORD: '/app/demo/auth-pages/reset-password',
    OTP: '/app/demo/auth-pages/otp',
    OTHER: '/app/demo/auth-pages/other',
  },
  // Settings
  SETTINGS: {
    COLORS: '/app/demo/settings/colors',
    THEME: '/app/demo/settings/theme',
    TYPOGRAPHY: '/app/demo/settings/typography',
    TRANSLATIONS: '/app/demo/settings/translations',
    ACCESSIBILITY: '/app/demo/settings/accessibility',
  },
} as const;

// ============================================================================
// STORE ROUTES
// ============================================================================
/**
 * Store domain — the non-vendor merch catalogue plus the legacy shop orders
 * and payments that consume it. Vendor-marketplace routes live under
 * /app/vendors instead.
 */
export const STORE_ROUTES = {
  BASE: '/app/store',
  DASHBOARD: '/app/store/dashboard',

  // Orders
  ORDERS: '/app/store/orders',
  ORDER_NEW: '/app/store/orders/new',
  ORDER_VIEW: (id: string | number) => `/app/store/orders/${id}` as const,
  ORDER_PICKING_LIST: '/app/store/orders/picking-list',

  // Products
  PRODUCTS: '/app/store/products',
  PRODUCT_NEW: '/app/store/products/new',
  PRODUCT_VIEW: (id: string | number) => `/app/store/products/${id}` as const,
  PRODUCTS_LOW_STOCK: '/app/store/products/low-stock',

  // Payments
  PAYMENTS: '/app/store/payments',
  PAYMENT_VIEW: (id: string | number) => `/app/store/payments/${id}` as const,

  // Customers — keyed by email, so always encode it
  CUSTOMERS: '/app/store/customers',
  CUSTOMER_VIEW: (email: string) =>
    `/app/store/customers/${encodeURIComponent(email)}` as const,
} as const;

// ============================================================================
// COMBINED ROUTES OBJECT
// ============================================================================
export const ROUTES = {
  AUTH: AUTH_ROUTES,
  MAIN: MAIN_ROUTES,
  PROJECTS: PROJECTS_ROUTES,
  TASKS: TASKS_ROUTES,
  MEMBERS: MEMBERS_ROUTES,
  USERS: USERS_ROUTES,
  PROCUREMENTS: PROCUREMENTS_ROUTES,
  MEETINGS: MEETINGS_ROUTES,
  CAMPUS_REPORT: CAMPUS_REPORT_ROUTES,
  ANNOUNCEMENTS: ANNOUNCEMENTS_ROUTES,
  STORE: STORE_ROUTES,
  ADMIN: ADMIN_ROUTES,
  DEMO: DEMO_ROUTES,
} as const;

// ============================================================================
// ROUTE HELPERS
// ============================================================================

/**
 * Check if a path matches a route pattern
 * @param path - Current path to check
 * @param route - Route pattern to match against
 * @returns True if the path matches the route pattern
 */
export function matchRoute(path: string, route: string): boolean {
  // Exact match
  if (path === route) return true;

  // Check if path starts with route (for nested routes)
  if (path.startsWith(route + '/')) return true;

  return false;
}

/**
 * Get the domain key from a path
 * @param path - Current path
 * @returns The domain key or null if not in a domain
 */
export function getDomainFromPath(
  path: string
): 'projects' | 'tasks' | 'members' | 'users' | 'procurements' | 'meetings' | 'campus-report' | 'demo' | null {
  if (path.startsWith('/app/projects')) return 'projects';
  if (path.startsWith('/app/tasks')) return 'tasks';
  if (path.startsWith('/app/members')) return 'members';
  if (path.startsWith('/app/users')) return 'users';
  if (path.startsWith('/app/procurements')) return 'procurements';
  if (path.startsWith('/app/meetings')) return 'meetings';
  if (path.startsWith('/app/campus-report')) return 'campus-report';
  if (path.startsWith('/app/demo')) return 'demo';
  return null;
}

/**
 * Check if a path is an auth route
 * @param path - Current path
 * @returns True if the path is an auth route
 */
export function isAuthRoute(path: string): boolean {
  return path.startsWith('/auth');
}

/**
 * Check if a path is a protected route (requires authentication)
 * @param path - Current path
 * @returns True if the path requires authentication
 */
export function isProtectedRoute(path: string): boolean {
  return path.startsWith('/app') || path === MAIN_ROUTES.PROFILE || path === MAIN_ROUTES.SECURITY_SETTINGS;
}

// Type exports for route values
export type AuthRoute = (typeof AUTH_ROUTES)[keyof typeof AUTH_ROUTES];
export type MainRoute = (typeof MAIN_ROUTES)[keyof typeof MAIN_ROUTES];
export type ProjectsRoute = (typeof PROJECTS_ROUTES)[keyof typeof PROJECTS_ROUTES];
export type StoreRoute = (typeof STORE_ROUTES)[keyof typeof STORE_ROUTES];
